import { useLanguage } from '../context/LanguageContext';

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Privacy Policy',
      subtitle: 'Data Protection Declaration',
      last_updated: 'Last updated: April 2026',
      intro_title: '1. Overview',
      intro: 'We take the protection of your personal data very seriously. We treat your personal data confidentially and in accordance with the Swiss Federal Act on Data Protection (FADP/DSG), the EU General Data Protection Regulation (GDPR), and this privacy policy.',
      controller_title: '2. Data Controller',
      controller: 'The data controller responsible for data processing on this website is:',
      collection_title: '3. Data Collection on Our Website',
      collection_server: 'Server Log Files',
      collection_server_text: 'Our web hosting provider automatically collects and stores information in server log files that your browser transmits automatically. These include: browser type and version, operating system, referrer URL, hostname of the accessing computer, and time of the server request. This data cannot be attributed to specific persons and is not combined with other data sources.',
      collection_booking: 'Booking Data',
      collection_booking_text: 'When you make a booking through our website, we collect: name, email address, phone number, pickup and drop-off locations, travel dates and times, number of passengers, and payment information. This data is processed for the purpose of fulfilling our transport service contract with you.',
      collection_contact: 'Contact Form',
      collection_contact_text: 'When you contact us via the contact form, we collect your name, email address, phone number, and message content. This data is processed to respond to your inquiry.',
      cookies_title: '4. Cookies',
      cookies_text: 'Our website uses cookies. Cookies are small text files stored on your device by your browser. They do not cause any damage and are used to make our website more user-friendly.',
      cookies_essential: 'Essential Cookies: Required for the basic functionality of the website (language preferences, session management).',
      cookies_analytics: 'Analytics Cookies: Help us understand how visitors interact with our website (PostHog analytics).',
      cookies_third: 'Third-Party Cookies: Used by integrated services such as Google Maps and SumUp payment gateway.',
      third_party_title: '5. Third-Party Services',
      google_maps: 'Google Maps',
      google_maps_text: 'We use Google Maps for address autocomplete and distance calculation. When you use these features, data is transmitted to Google LLC. Please refer to Google\'s privacy policy: https://policies.google.com/privacy',
      sumup: 'SumUp (Payment Processing)',
      sumup_text: 'We use SumUp for processing card payments. When you make a card payment, your payment data is processed directly by SumUp Payments Limited. We do not store your full card details. Please refer to SumUp\'s privacy policy: https://www.sumup.com/privacy',
      sendgrid: 'SendGrid (Email Service)',
      sendgrid_text: 'We use SendGrid (Twilio) for sending booking confirmations and notifications. Your email address and booking details are transmitted to SendGrid for this purpose. Please refer to Twilio\'s privacy policy: https://www.twilio.com/legal/privacy',
      rights_title: '6. Your Rights',
      rights_text: 'Under the FADP/DSG and GDPR, you have the following rights:',
      rights_list: [
        'Right of access to your personal data',
        'Right to rectification of inaccurate data',
        'Right to erasure ("right to be forgotten")',
        'Right to restriction of processing',
        'Right to data portability',
        'Right to object to processing',
      ],
      rights_contact: 'To exercise these rights, please contact us at etourtransfer@gmail.com',
      retention_title: '7. Data Retention',
      retention_text: 'We retain your booking data for the duration required by Swiss commercial and tax law (typically 10 years). Contact form messages are retained for 2 years. You may request deletion of your data at any time, subject to legal retention obligations.',
      security_title: '8. Data Security',
      security_text: 'We use industry-standard SSL/TLS encryption to protect data transmitted between your browser and our servers. We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, or destruction.',
      changes_title: '9. Changes to This Policy',
      changes_text: 'We reserve the right to update this privacy policy. We will notify you of significant changes by posting the new policy on this page with an updated revision date.',
      contact_title: '10. Contact',
      contact_text: 'If you have questions about this privacy policy or our data processing practices, please contact us:',
    },
    de: {
      title: 'Datenschutzerklärung',
      subtitle: 'Datenschutz',
      last_updated: 'Letzte Aktualisierung: April 2026',
      intro_title: '1. Übersicht',
      intro: 'Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend dem Schweizer Bundesgesetz über den Datenschutz (DSG), der EU-Datenschutz-Grundverordnung (DSGVO) sowie dieser Datenschutzerklärung.',
      controller_title: '2. Verantwortliche Stelle',
      controller: 'Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:',
      collection_title: '3. Datenerfassung auf unserer Website',
      collection_server: 'Server-Logdateien',
      collection_server_text: 'Unser Webhosting-Provider erhebt und speichert automatisch Informationen in Server-Logdateien, die Ihr Browser automatisch übermittelt. Dies sind: Browsertyp und -version, Betriebssystem, Referrer-URL, Hostname des zugreifenden Rechners und Zeitpunkt der Serveranfrage. Diese Daten sind keiner bestimmten Person zuordenbar und werden nicht mit anderen Datenquellen zusammengeführt.',
      collection_booking: 'Buchungsdaten',
      collection_booking_text: 'Wenn Sie eine Buchung über unsere Website vornehmen, erfassen wir: Name, E-Mail-Adresse, Telefonnummer, Abhol- und Zielorte, Reisedaten und -zeiten, Anzahl der Passagiere sowie Zahlungsinformationen. Diese Daten werden zur Erfüllung unseres Beförderungsvertrages mit Ihnen verarbeitet.',
      collection_contact: 'Kontaktformular',
      collection_contact_text: 'Wenn Sie uns über das Kontaktformular kontaktieren, erfassen wir Ihren Namen, Ihre E-Mail-Adresse, Ihre Telefonnummer und den Nachrichteninhalt. Diese Daten werden zur Beantwortung Ihrer Anfrage verarbeitet.',
      cookies_title: '4. Cookies',
      cookies_text: 'Unsere Website verwendet Cookies. Cookies sind kleine Textdateien, die von Ihrem Browser auf Ihrem Gerät gespeichert werden. Sie richten keinen Schaden an und dienen dazu, unsere Website benutzerfreundlicher zu gestalten.',
      cookies_essential: 'Essentielle Cookies: Erforderlich für die grundlegende Funktionalität der Website (Spracheinstellungen, Sitzungsverwaltung).',
      cookies_analytics: 'Analyse-Cookies: Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren (PostHog Analytics).',
      cookies_third: 'Drittanbieter-Cookies: Werden von integrierten Diensten wie Google Maps und SumUp-Zahlungsgateway verwendet.',
      third_party_title: '5. Drittanbieterdienste',
      google_maps: 'Google Maps',
      google_maps_text: 'Wir verwenden Google Maps für die Adressvervollständigung und Entfernungsberechnung. Bei Nutzung dieser Funktionen werden Daten an Google LLC übermittelt. Bitte beachten Sie die Datenschutzerklärung von Google: https://policies.google.com/privacy',
      sumup: 'SumUp (Zahlungsabwicklung)',
      sumup_text: 'Wir verwenden SumUp für die Abwicklung von Kartenzahlungen. Bei einer Kartenzahlung werden Ihre Zahlungsdaten direkt von SumUp Payments Limited verarbeitet. Wir speichern keine vollständigen Kartendaten. Bitte beachten Sie die Datenschutzerklärung von SumUp: https://www.sumup.com/privacy',
      sendgrid: 'SendGrid (E-Mail-Dienst)',
      sendgrid_text: 'Wir verwenden SendGrid (Twilio) zum Versand von Buchungsbestätigungen und Benachrichtigungen. Ihre E-Mail-Adresse und Buchungsdetails werden zu diesem Zweck an SendGrid übermittelt. Bitte beachten Sie die Datenschutzerklärung von Twilio: https://www.twilio.com/legal/privacy',
      rights_title: '6. Ihre Rechte',
      rights_text: 'Gemäss DSG und DSGVO haben Sie folgende Rechte:',
      rights_list: [
        'Recht auf Auskunft über Ihre gespeicherten Daten',
        'Recht auf Berichtigung unrichtiger Daten',
        'Recht auf Löschung ("Recht auf Vergessenwerden")',
        'Recht auf Einschränkung der Verarbeitung',
        'Recht auf Datenübertragbarkeit',
        'Recht auf Widerspruch gegen die Verarbeitung',
      ],
      rights_contact: 'Zur Ausübung dieser Rechte kontaktieren Sie uns bitte unter etourtransfer@gmail.com',
      retention_title: '7. Datenspeicherung',
      retention_text: 'Wir bewahren Ihre Buchungsdaten für die nach Schweizer Handels- und Steuerrecht erforderliche Dauer auf (in der Regel 10 Jahre). Kontaktformular-Nachrichten werden 2 Jahre aufbewahrt. Sie können jederzeit die Löschung Ihrer Daten beantragen, vorbehaltlich gesetzlicher Aufbewahrungspflichten.',
      security_title: '8. Datensicherheit',
      security_text: 'Wir verwenden branchenübliche SSL/TLS-Verschlüsselung zum Schutz der zwischen Ihrem Browser und unseren Servern übertragenen Daten. Wir setzen angemessene technische und organisatorische Massnahmen ein, um Ihre personenbezogenen Daten gegen unbefugten Zugriff, Veränderung oder Vernichtung zu schützen.',
      changes_title: '9. Änderungen dieser Erklärung',
      changes_text: 'Wir behalten uns das Recht vor, diese Datenschutzerklärung zu aktualisieren. Über wesentliche Änderungen informieren wir Sie durch Veröffentlichung der neuen Erklärung auf dieser Seite mit aktualisiertem Datum.',
      contact_title: '10. Kontakt',
      contact_text: 'Bei Fragen zu dieser Datenschutzerklärung oder unserer Datenverarbeitung kontaktieren Sie uns bitte:',
    },
    it: {
      title: 'Informativa sulla Privacy',
      subtitle: 'Protezione dei Dati',
      last_updated: 'Ultimo aggiornamento: Aprile 2026',
      intro_title: '1. Panoramica',
      intro: 'Prendiamo molto seriamente la protezione dei vostri dati personali. Trattiamo i vostri dati personali in modo confidenziale e in conformità con la Legge federale svizzera sulla protezione dei dati (LPD), il Regolamento generale sulla protezione dei dati dell\'UE (GDPR) e la presente informativa sulla privacy.',
      controller_title: '2. Titolare del Trattamento',
      controller: 'Il titolare del trattamento dei dati su questo sito web è:',
      collection_title: '3. Raccolta Dati sul Nostro Sito',
      collection_server: 'File di log del server',
      collection_server_text: 'Il nostro provider di web hosting raccoglie e archivia automaticamente informazioni nei file di log del server trasmessi automaticamente dal vostro browser. Questi includono: tipo e versione del browser, sistema operativo, URL di riferimento, nome host del computer di accesso e ora della richiesta del server.',
      collection_booking: 'Dati di prenotazione',
      collection_booking_text: 'Quando effettuate una prenotazione tramite il nostro sito web, raccogliamo: nome, indirizzo email, numero di telefono, luoghi di partenza e arrivo, date e orari di viaggio, numero di passeggeri e informazioni di pagamento.',
      collection_contact: 'Modulo di contatto',
      collection_contact_text: 'Quando ci contattate tramite il modulo di contatto, raccogliamo il vostro nome, indirizzo email, numero di telefono e il contenuto del messaggio.',
      cookies_title: '4. Cookie',
      cookies_text: 'Il nostro sito web utilizza cookie. I cookie sono piccoli file di testo memorizzati sul vostro dispositivo dal vostro browser. Non causano danni e servono a rendere il nostro sito web più facile da usare.',
      cookies_essential: 'Cookie essenziali: Necessari per la funzionalità di base del sito web (preferenze linguistiche, gestione sessione).',
      cookies_analytics: 'Cookie analitici: Ci aiutano a capire come i visitatori interagiscono con il nostro sito web (PostHog analytics).',
      cookies_third: 'Cookie di terze parti: Utilizzati da servizi integrati come Google Maps e il gateway di pagamento SumUp.',
      third_party_title: '5. Servizi di Terze Parti',
      google_maps: 'Google Maps',
      google_maps_text: 'Utilizziamo Google Maps per il completamento automatico degli indirizzi e il calcolo delle distanze. Quando utilizzate queste funzionalità, i dati vengono trasmessi a Google LLC.',
      sumup: 'SumUp (Elaborazione Pagamenti)',
      sumup_text: 'Utilizziamo SumUp per l\'elaborazione dei pagamenti con carta. I vostri dati di pagamento vengono elaborati direttamente da SumUp Payments Limited.',
      sendgrid: 'SendGrid (Servizio Email)',
      sendgrid_text: 'Utilizziamo SendGrid (Twilio) per l\'invio di conferme di prenotazione e notifiche.',
      rights_title: '6. I Vostri Diritti',
      rights_text: 'Ai sensi della LPD e del GDPR, avete i seguenti diritti:',
      rights_list: [
        'Diritto di accesso ai vostri dati personali',
        'Diritto di rettifica dei dati inesatti',
        'Diritto alla cancellazione ("diritto all\'oblio")',
        'Diritto alla limitazione del trattamento',
        'Diritto alla portabilità dei dati',
        'Diritto di opposizione al trattamento',
      ],
      rights_contact: 'Per esercitare questi diritti, contattateci a etourtransfer@gmail.com',
      retention_title: '7. Conservazione dei Dati',
      retention_text: 'Conserviamo i vostri dati di prenotazione per il periodo richiesto dalla legge commerciale e fiscale svizzera (tipicamente 10 anni).',
      security_title: '8. Sicurezza dei Dati',
      security_text: 'Utilizziamo la crittografia SSL/TLS standard del settore per proteggere i dati trasmessi tra il vostro browser e i nostri server.',
      changes_title: '9. Modifiche a Questa Informativa',
      changes_text: 'Ci riserviamo il diritto di aggiornare questa informativa sulla privacy.',
      contact_title: '10. Contatto',
      contact_text: 'Per domande su questa informativa o sulle nostre pratiche di trattamento dei dati, contattateci:',
    },
  };

  const t = content[language] || content.en;

  const Section = ({ title, children }) => (
    <section className="mb-8">
      <h2 className="text-lg text-[#D4AF37] mb-3 font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{title}</h2>
      {children}
    </section>
  );

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#0A0A0A]" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-4">{t.subtitle}</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="privacy-title">
            {t.title}
          </h1>
          <p className="text-[#A1A1AA] text-sm mt-4">{t.last_updated}</p>
        </div>

        <div className="bg-[#121212] border border-white/10 p-8 sm:p-10">
          <Section title={t.intro_title}>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{t.intro}</p>
          </Section>

          <Section title={t.controller_title}>
            <p className="text-[#A1A1AA] text-sm mb-2">{t.controller}</p>
            <div className="text-white text-sm space-y-1">
              <p className="font-semibold">Exclusive Tour Transfer Dumitru</p>
              <p className="text-[#A1A1AA]">Bahnhofstrasse 1, 8560 Marstetten, Switzerland</p>
              <p className="text-[#A1A1AA]">etourtransfer@gmail.com | +41 79 927 07 31</p>
              <p className="text-[#A1A1AA]">CHE-468.004.384</p>
            </div>
          </Section>

          <Section title={t.collection_title}>
            <h3 className="text-white text-sm font-semibold mb-2">{t.collection_server}</h3>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{t.collection_server_text}</p>
            <h3 className="text-white text-sm font-semibold mb-2">{t.collection_booking}</h3>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{t.collection_booking_text}</p>
            <h3 className="text-white text-sm font-semibold mb-2">{t.collection_contact}</h3>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{t.collection_contact_text}</p>
          </Section>

          <Section title={t.cookies_title}>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{t.cookies_text}</p>
            <ul className="space-y-2 text-[#A1A1AA] text-sm">
              <li className="flex items-start gap-2"><span className="text-[#D4AF37]">&bull;</span>{t.cookies_essential}</li>
              <li className="flex items-start gap-2"><span className="text-[#D4AF37]">&bull;</span>{t.cookies_analytics}</li>
              <li className="flex items-start gap-2"><span className="text-[#D4AF37]">&bull;</span>{t.cookies_third}</li>
            </ul>
          </Section>

          <Section title={t.third_party_title}>
            <h3 className="text-white text-sm font-semibold mb-2">{t.google_maps}</h3>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{t.google_maps_text}</p>
            <h3 className="text-white text-sm font-semibold mb-2">{t.sumup}</h3>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{t.sumup_text}</p>
            <h3 className="text-white text-sm font-semibold mb-2">{t.sendgrid}</h3>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{t.sendgrid_text}</p>
          </Section>

          <Section title={t.rights_title}>
            <p className="text-[#A1A1AA] text-sm mb-3">{t.rights_text}</p>
            <ul className="space-y-2 text-[#A1A1AA] text-sm mb-3">
              {t.rights_list.map((right, i) => (
                <li key={i} className="flex items-start gap-2"><span className="text-[#D4AF37]">&bull;</span>{right}</li>
              ))}
            </ul>
            <p className="text-[#D4AF37] text-sm">{t.rights_contact}</p>
          </Section>

          <Section title={t.retention_title}>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{t.retention_text}</p>
          </Section>

          <Section title={t.security_title}>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{t.security_text}</p>
          </Section>

          <Section title={t.changes_title}>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{t.changes_text}</p>
          </Section>

          <Section title={t.contact_title}>
            <p className="text-[#A1A1AA] text-sm mb-3">{t.contact_text}</p>
            <div className="text-[#A1A1AA] text-sm space-y-1">
              <p className="text-white font-semibold">Exclusive Tour Transfer Dumitru</p>
              <p>Bahnhofstrasse 1, 8560 Marstetten, Switzerland</p>
              <p>etourtransfer@gmail.com | +41 79 927 07 31</p>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
